import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  // --- COD TIMER LOGIC ADDED: Property to hold the interval ---
  #eidTimerInterval = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // PROGRESS BAR: Listen for quantity updates to refresh the bar
    document.addEventListener('cart:updated', this.#handleProgressBarUpdate);

    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // --- COD TIMER LOGIC ADDED: Start the global timer ---
    this.#startContinuousTimer();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // PROGRESS BAR: Clean up the listener
    document.removeEventListener('cart:updated', this.#handleProgressBarUpdate);

    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();

    // --- COD TIMER LOGIC ADDED: Clean up memory if closed ---
    if (this.#eidTimerInterval) clearInterval(this.#eidTimerInterval);
  }

  // --- COD TIMER LOGIC ADDED: The native timer function ---
  #startContinuousTimer = () => {
    if (this.#eidTimerInterval) return; // Prevent duplicate timers

    const updateTick = () => {
      // Look for the timer element in the current DOM
      const timerElement = document.getElementById('live-timer-text');
      if (!timerElement) return; // If drawer is updating or closed, safely skip

      const cutoffUnix = parseInt(timerElement.getAttribute('data-cutoff'));
      if (!cutoffUnix) return;

      const nowUnix = Math.floor(Date.now() / 1000);
      const diff = cutoffUnix - nowUnix;

      if (diff <= 0) {
        timerElement.innerHTML = "00d : 00h : 00m : 00s";
        return;
      }

      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;

      const dStr = d < 10 ? '0' + d : d;
      const hStr = h < 10 ? '0' + h : h;
      const mStr = m < 10 ? '0' + m : m;
      const sStr = s < 10 ? '0' + s : s;

      timerElement.innerHTML = `${dStr}d : ${hStr}h : ${mStr}m : ${sStr}s`;
    };

    // 1. Run immediately so it never says "Calculating..."
    updateTick();

    // 2. Force an immediate update anytime the drawer is opened
    this.addEventListener(DialogOpenEvent.eventName, updateTick);

    // 3. Force an update right after an AJAX cart refresh
    document.addEventListener('cart:updated', () => {
       setTimeout(updateTick, 150); // Tiny delay to let the new HTML load in
    });

    // 4. Tick continuously in the background
    this.#eidTimerInterval = setInterval(updateTick, 1000);
  };
  // --------------------------------------------------------

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    // --- SCROLL JUMP FIX ---
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (!window.history.state?.cartDrawerOpen) {
      window.history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open, updates progress, and announces count.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // PROGRESS BAR: Update the bar immediately when an item is added
    this.#handleProgressBarUpdate();

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // ACCESSIBILITY: Announce cart count update
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * PROGRESS BAR LOGIC:
   * Fetches the updated cart drawer section and replaces the progress bar HTML container.
   */
  #handleProgressBarUpdate = async () => {
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) return;
      
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      const newProgress = html.querySelector('#cart-drawer-progress-container');
      const currentProgress = document.querySelector('#cart-drawer-progress-container');
      
      if (newProgress && currentProgress) {
        currentProgress.innerHTML = newProgress.innerHTML;
      }
    } catch (error) {
      console.error('Failed to update progress bar:', error);
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}